import { create } from "zustand";
import {
    dismissAssistantIndexJob,
    getAssistantIndexJobs,
} from "@/lib/actions/assistant-index-actions";
import type { AssistantIndexJobView } from "@/lib/ai/assistant-index-job-types";

interface AssistantIndexState {
    ownerId: string | null;
    jobs: AssistantIndexJobView[];
    revision: number;
    refreshRequestId: number;
    pendingDismissals: string[];
    setOwner: (ownerId: string | null) => void;
    refresh: (ownerId: string) => Promise<void>;
    upsert: (job: AssistantIndexJobView) => void;
    dismiss: (jobId: string) => Promise<void>;
}

export const useAssistantIndexStore = create<AssistantIndexState>((set, get) => ({
    ownerId: null,
    jobs: [],
    revision: 0,
    refreshRequestId: 0,
    pendingDismissals: [],
    setOwner: (ownerId) => {
        set((state) =>
            state.ownerId === ownerId
                ? state
                : {
                      ...state,
                      ownerId,
                      jobs: [],
                      revision: state.revision + 1,
                      refreshRequestId: state.refreshRequestId + 1,
                      pendingDismissals: [],
                  },
        );
    },
    refresh: async (ownerId) => {
        const snapshot = get();
        if (snapshot.ownerId !== ownerId) return;

        const requestId = snapshot.refreshRequestId + 1;
        const revision = snapshot.revision;
        set((state) =>
            state.ownerId === ownerId
                ? { ...state, refreshRequestId: requestId }
                : state,
        );

        try {
            const jobs = await getAssistantIndexJobs();
            set((state) =>
                state.ownerId === ownerId &&
                state.refreshRequestId === requestId &&
                state.revision === revision
                    ? {
                          ...state,
                          jobs: jobs.filter(
                              (job) =>
                                  !state.pendingDismissals.includes(job.id),
                          ),
                      }
                    : state,
            );
        } catch {
            // Keep the last durable snapshot through transient auth/network loss.
        }
    },
    upsert: (job) => {
        set((state) =>
            state.ownerId === job.authorId &&
            !state.pendingDismissals.includes(job.id)
                ? {
                      ...state,
                      jobs: [
                          job,
                          ...state.jobs.filter((item) => item.id !== job.id),
                      ],
                      revision: state.revision + 1,
                  }
                : state,
        );
    },
    dismiss: async (jobId) => {
        const ownerId = get().ownerId;
        set((state) => ({
            jobs: state.jobs.filter((job) => job.id !== jobId),
            revision: state.revision + 1,
            pendingDismissals: state.pendingDismissals.includes(jobId)
                ? state.pendingDismissals
                : [...state.pendingDismissals, jobId],
        }));
        try {
            await dismissAssistantIndexJob(jobId);
        } catch {
            set((state) =>
                state.pendingDismissals.includes(jobId)
                    ? {
                          ...state,
                          pendingDismissals: state.pendingDismissals.filter(
                              (id) => id !== jobId,
                          ),
                          revision: state.revision + 1,
                      }
                    : state,
            );
            if (ownerId) await get().refresh(ownerId);
            return;
        }
        set((state) =>
            state.pendingDismissals.includes(jobId)
                ? {
                      ...state,
                      pendingDismissals: state.pendingDismissals.filter(
                          (id) => id !== jobId,
                      ),
                      revision: state.revision + 1,
                  }
                : state,
        );
    },
}));
